/* src/modules/directory/EmployeeProfileModal.js */
import React, { useState, useEffect, useRef } from 'react'; 
import { doc, serverTimestamp, collection, addDoc, onSnapshot, deleteDoc, query, orderBy, getDoc, setDoc, writeBatch, where, getDocs } from 'firebase/firestore';
import { auth, db, storage } from '../../firebase/config';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { TrashIcon, PaperclipIcon, TrophyIcon, TrendingUpIcon, StarIcon, SearchIcon, UserIcon, XIcon, CubeIcon } from '../../components/icons/Icons';
import EmployeeTrainingStats from '../pd/EmployeeTrainingStats'; 

// --- HELPERS ---
const BRAND_COLORS = { orange: '#F4A866', teal: '#20BEC6', red: '#D95448' };

const LEVEL_TIERS = [
    { max: 50, label: "Rookie" },
    { max: 100, label: "Rising Star" },
    { max: 200, label: "Captain" },
    { max: 300, label: "Rockstar" },
    { max: 500, label: "Superstar" },
    { max: Infinity, label: "Legend" }
];

const getStatusLabel = (points) => {
    const p = Number(points) || 0;
    const tier = LEVEL_TIERS.find(t => p < t.max) || LEVEL_TIERS[LEVEL_TIERS.length - 1];
    return tier.label;
};

const safeDate = (val) => {
    if (!val) return '';
    if (typeof val.toDate === 'function') return val.toDate().toISOString().split('T')[0];
    if (val instanceof Date) return val.toISOString().split('T')[0];
    if (typeof val === 'string') return val;
    return '';
};

const displayDate = (val) => {
    if (!val) return '-';
    if (typeof val.toDate === 'function') return val.toDate().toLocaleDateString();
    if (val instanceof Date) return val.toLocaleDateString();
    if (typeof val === 'string' && val.includes('-')) return new Date(val).toLocaleDateString();
    return '-';
};

const UserAvatar = ({ url, name, className = "h-8 w-8" }) => {
    const [imgError, setImgError] = useState(false);
    if (url && !imgError) {
        return <img src={url} alt={name} className={`${className} rounded-full object-cover border border-gray-200`} onError={() => setImgError(true)} />;
    }
    return (
        <div className={`${className} rounded-full bg-blue-100 flex items-center justify-center text-[#213368] font-bold text-xs shrink-0 border border-blue-200`}>
            {name ? name.charAt(0).toUpperCase() : <UserIcon className="h-3 w-3" />}
        </div>
    );
};

export default function EmployeeProfileModal({ appId, allUsers, existingUser, onClose, readOnly = false }) {
    const [activeTab, setActiveTab] = useState('official');
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [formData, setFormData] = useState(null);
    const [error, setError] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [trophies, setTrophies] = useState([]);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [docUpload, setDocUpload] = useState({ file: null, title: '' });

    const userId = existingUser?.id || existingUser?.uid;

    useEffect(() => {
        if (!userId) return;

        const fetchData = async () => {
            try {
                // 1. Fetch Profile
                const userRef = doc(db, `/artifacts/${appId}/users/${userId}`);
                const profileRef = doc(db, `/artifacts/${appId}/users/${userId}/profiles/details`);
                const [userSnap, profileSnap] = await Promise.all([getDoc(userRef), getDoc(profileRef)]);

                if (!userSnap.exists()) throw new Error("User profile not found.");

                const coreData = userSnap.data();
                const profileData = profileSnap.exists() ? profileSnap.data() : {};

                setFormData({
                    // Core Data Defaults
                    employeeId: coreData.employeeId || '',
                    designation: coreData.designation || '',
                    managerId: coreData.managerId || '',
                    location: coreData.location || '',
                    phoneCountryCode: coreData.phoneCountryCode || '',
                    phoneNumber: coreData.phoneNumber || '',
                    panCardNumber: coreData.panCardNumber || '',
                    employmentLevel: coreData.employmentLevel || '',
                    employmentGrade: coreData.employmentGrade || '',
                    employeeStatus: coreData.employeeStatus || 'Active',
                    photoURL: coreData.photoURL || '',
                    
                    // Profile Data Defaults
                    middleName: profileData.middleName || '',
                    parentName: profileData.parentName || '',
                    gender: profileData.gender || '',
                    maritalStatus: profileData.maritalStatus || '',
                    bloodGroup: profileData.bloodGroup || '',
                    isDifferentlyAbled: profileData.isDifferentlyAbled || 'No',
                    personalEmail: profileData.personalEmail || '',
                    personalMobile: profileData.personalMobile || '',
                    residentialAddress: profileData.residentialAddress || '',
                    nationality: profileData.nationality || '',
                    aadharNumber: profileData.aadharNumber || '',
                    passportNumber: profileData.passportNumber || '',
                    emergencyContactName: profileData.emergencyContactName || '',
                    emergencyContactNumber: profileData.emergencyContactNumber || '',
                    emergencyContactRelation: profileData.emergencyContactRelation || '',

                    // Dates & Points
                    name: coreData.name || '',
                    email: coreData.email || '',
                    department: coreData.department || '',
                    dateOfJoining: safeDate(coreData.dateOfJoining),
                    confirmationDate: safeDate(coreData.confirmationDate),
		    lastWorkingDate: safeDate(coreData.lastWorkingDate),
                    dateOfBirth: safeDate(profileData.dateOfBirth),
                    points: Number(coreData.points) || 0,
                    badges: coreData.badges || {},
                    exitClearance: coreData.exitClearance || null,
 		    journey: coreData.journey || []
                });

                // 2. Fetch Reviews & Trophies
                const reviewsRef = collection(db, `artifacts/${appId}/performance_reviews`);
                const qReviews = query(reviewsRef, where('userId', '==', userId), orderBy('createdAt', 'desc'));
                const reviewsSnap = await getDocs(qReviews);
                setReviews(reviewsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

                const challengesRef = collection(db, `artifacts/${appId}/challenges`);
                const qTrophies = query(challengesRef, where('winners', 'array-contains', userId));
                const trophiesSnap = await getDocs(qTrophies);
                setTrophies(trophiesSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            } catch (err) {
                console.error("Failed to fetch data:", err);
                setError("Could not load profile.");
            }
        };

        fetchData();

        // 4. Docs Listener
        const docsRef = collection(db, `/artifacts/${appId}/users/${userId}/documents`);
        const qDocs = query(docsRef, orderBy('uploadedAt', 'desc'));
        const unsub = onSnapshot(qDocs, (snap) => {
            setDocuments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsDataLoading(false);
        });

        return () => unsub();
    }, [appId, userId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (readOnly) return;
        setIsSaving(true);
        setMessage('');
        try {
            const userRef = doc(db, `/artifacts/${appId}/users`, userId);
            const profileRef = doc(db, `/artifacts/${appId}/users/${userId}/profiles/details`);
            const batch = writeBatch(db);

            let birthDay = null, birthMonth = null;
            if (formData.dateOfBirth) {
                const parts = formData.dateOfBirth.split('-');
                if (parts.length === 3) {
                    birthMonth = parseInt(parts[1], 10); 
                    birthDay = parseInt(parts[2], 10);
                }
            }

            batch.update(userRef, {
                employeeId: formData.employeeId || null,
                designation: formData.designation || null,
                managerId: formData.managerId || null,
		managerName: formData.managerName || null,
                location: formData.location || null,
                phoneCountryCode: formData.phoneCountryCode || null,
                phoneNumber: formData.phoneNumber || null,             
                panCardNumber: formData.panCardNumber || null,	
                dateOfJoining: formData.dateOfJoining ? new Date(formData.dateOfJoining) : null,
                confirmationDate: formData.confirmationDate ? new Date(formData.confirmationDate) : null,
                lastWorkingDate: formData.lastWorkingDate ? new Date(formData.lastWorkingDate) : null,
                employmentLevel: formData.employmentLevel || null,
                employmentGrade: formData.employmentGrade || null,
                employeeStatus: formData.employeeStatus || 'Active',
                photoURL: formData.photoURL || null,
                updatedAt: serverTimestamp(),
                birthDay: birthDay || null, 
                birthMonth: birthMonth || null,
 		journey: formData.journey || []
            });

            const profilePayload = {
                middleName: formData.middleName || null,
                parentName: formData.parentName || null,
                gender: formData.gender || null,
                dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : null,
                maritalStatus: formData.maritalStatus || null,
                bloodGroup: formData.bloodGroup || null,
                isDifferentlyAbled: formData.isDifferentlyAbled || 'No',
                phoneCountryCode: formData.phoneCountryCode || null,
                phoneNumber: formData.phoneNumber || null,
                personalEmail: formData.personalEmail || null,
                personalMobile: formData.personalMobile || null,
                residentialAddress: formData.residentialAddress || null,
                nationality: formData.nationality || null,
                aadharNumber: formData.aadharNumber || null,
                panCardNumber: formData.panCardNumber || null,
                passportNumber: formData.passportNumber || null,
                emergencyContactName: formData.emergencyContactName || null,
                emergencyContactNumber: formData.emergencyContactNumber || null,
                emergencyContactRelation: formData.emergencyContactRelation || null,
            };
            batch.set(profileRef, profilePayload, { merge: true });
            
            await batch.commit();
            setMessage('Successfully saved!');
            setTimeout(onClose, 1500);
        } catch (error) {
            console.error("Error saving:", error);
            setMessage(`Error: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDocumentUpload = async () => {
        if (!docUpload.title.trim() || !docUpload.file) return alert('Missing file or title.');
        setIsSaving(true);
        try {
            const storagePath = `employee-documents/${userId}/${Date.now()}_${docUpload.file.name}`;
            const storageRef = ref(storage, storagePath);
            const metadata = { customMetadata: { 'appId': appId } };
            const uploadTask = await uploadBytesResumable(storageRef, docUpload.file, metadata);
            const downloadURL = await getDownloadURL(uploadTask.ref);
            await addDoc(collection(db, `/artifacts/${appId}/users/${userId}/documents`), {
                title: docUpload.title,
                url: downloadURL,
                fileName: docUpload.file.name,
                storagePath: storagePath,
                uploadedAt: serverTimestamp(),
            });
            setDocUpload({ file: null, title: '' });
        } catch (error) {
            console.error(error);
            setMessage(`Upload Error: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDocumentDelete = async (docToDelete) => {
        if (!window.confirm(`Delete "${docToDelete.title}"?`)) return;
        try {
            await deleteObject(ref(storage, docToDelete.storagePath));
            await deleteDoc(doc(db, `/artifacts/${appId}/users/${userId}/documents`, docToDelete.id));
        } catch (error) {
            console.error(error);
            setMessage(`Delete Error: ${error.message}`);
        }
    };

    if (error) return <div className="p-8 text-center text-red-600 bg-white rounded-lg">{error}</div>;
    if (!formData) return <div className="p-8 text-center bg-white rounded-lg">Loading...</div>;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]">
                
                {/* HEADER */}
                <ProfileHeader formData={formData} />

                {/* TABS */}
                <div className="border-b border-gray-200 px-6">
                    <nav className="-mb-px flex space-x-6 overflow-x-auto">
                        <TabButton activeTab={activeTab} setActiveTab={setActiveTab} id="official" label="Official" />
                        <TabButton activeTab={activeTab} setActiveTab={setActiveTab} id="personal" label="Personal" />
                        <TabButton activeTab={activeTab} setActiveTab={setActiveTab} id="contact" label="Contact & ID" />
                        <TabButton activeTab={activeTab} setActiveTab={setActiveTab} id="documents" label="Documents" />
  			<TabButton activeTab={activeTab} setActiveTab={setActiveTab} id="journey" label="Journey" />
                        <TabButton activeTab={activeTab} setActiveTab={setActiveTab} id="performance" label="Performance" />
                        <TabButton activeTab={activeTab} setActiveTab={setActiveTab} id="training" label="Training" />
                        <TabButton activeTab={activeTab} setActiveTab={setActiveTab} id="recognition" label="Recognitions" />
                        <TabButton activeTab={activeTab} setActiveTab={setActiveTab} id="assets" label="IT Assets" />
                        <TabButton activeTab={activeTab} setActiveTab={setActiveTab} id="exit" label="Exit" />
                     </nav>
                </div>

                {/* CONTENT */}
                <div className="p-6 h-[55vh] overflow-y-auto bg-gray-50">
                    <form id="hrForm" onSubmit={handleSubmit}>
                        {activeTab === 'official' && <OfficialInfoTab formData={formData} handleChange={handleChange} readOnly={readOnly} allUsers={allUsers} existingUser={existingUser} />}
                        {activeTab === 'personal' && <PersonalInfoTab formData={formData} handleChange={handleChange} readOnly={readOnly} />}
                        {activeTab === 'contact' && <ContactInfoTab formData={formData} handleChange={handleChange} readOnly={readOnly} />}
			{activeTab === 'journey' && <JourneyTab formData={formData} handleChange={handleChange} readOnly={readOnly} />}
                        {activeTab === 'recognition' && <RecognitionTab formData={formData} trophies={trophies} />}
                        {activeTab === 'performance' && <PerformanceTab reviews={reviews} />}
                        {activeTab === 'training' && <div className="p-2"><EmployeeTrainingStats userId={userId} appId={appId} /></div>}
                        {activeTab === 'documents' && <DocumentsManagerTab documents={documents} isLoading={isDataLoading} docUpload={docUpload} setDocUpload={setDocUpload} onUpload={handleDocumentUpload} onDelete={handleDocumentDelete} readOnly={readOnly} isSaving={isSaving} />}
                         {activeTab === 'assets' && <UserAssetsTab appId={appId} userId={userId} />}
			{activeTab === 'exit' && <ExitTab formData={formData} handleChange={handleChange} readOnly={readOnly} />}
                    </form>
                </div>

                {/* FOOTER */}
                <div className="px-6 py-4 border-t border-gray-200 bg-white flex justify-between items-center">
                    <div className="text-sm font-medium text-green-600">{message}</div>
                    <div className="flex space-x-3">
                        <button type="button" onClick={onClose} disabled={isSaving} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">Close</button>
                        {!readOnly && (
                            <button type="submit" form="hrForm" disabled={isSaving} className="px-6 py-2 bg-[#20BEC6] text-white rounded-lg hover:opacity-90 font-medium disabled:opacity-50">
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- SUB COMPONENTS ---

// ✅ NEW ASSETS TAB COMPONENT
function UserAssetsTab({ appId, userId }) {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAssets = async () => {
            try {
                const q = query(
                    collection(db, `artifacts/${appId}/it_assets`),
                    where('assignedToUserId', '==', userId),
                    where('status', 'in', ['Active', 'Assigned'])
                );
                const snapshot = await getDocs(q);
                setAssets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                console.error("Error fetching assets:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAssets();
    }, [appId, userId]);

    if (loading) return <div className="text-center py-8 text-gray-400">Loading assets...</div>;
    if (assets.length === 0) return <div className="text-center py-10 bg-gray-50 rounded-lg text-gray-400">No IT assets currently assigned.</div>;

    return (
        <div className="space-y-3">
            {assets.map(asset => (
                <div key={asset.id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex justify-between items-center">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold uppercase tracking-wider bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-100">
                                {asset.category}
                            </span>
                            <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1 rounded">{asset.assetTag}</span>
                        </div>
                        <p className="font-bold text-[#213368] text-sm">
                            {asset.category === 'License / SaaS' ? asset.url : `${asset.vendor} ${asset.model}`}
                        </p>
                        {asset.serialNumber && (
                            <p className="text-xs text-gray-400 mt-0.5">SN: {asset.serialNumber}</p>
                        )}
                    </div>
                    <div className="text-right">
                        <span className="block text-xs text-gray-400">Assigned</span>
                        <span className="text-sm font-medium text-gray-700">
                            {asset.assignmentDate ? new Date(asset.assignmentDate).toLocaleDateString() : '-'}
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
}

function ProfileHeader({ formData }) {
    return (
        <div className="bg-gradient-to-r from-[#213368] to-[#20BEC6] p-6 flex items-center shadow-md z-10">
            <img 
                src={formData.photoURL || `https://ui-avatars.com/api/?name=${formData.name}&background=random`} 
                className="h-20 w-20 rounded-full border-4 border-white shadow-lg bg-white object-cover flex-shrink-0"
                referrerPolicy="no-referrer"
                alt={formData.name} 
            />
            <div className="ml-5">
                <h2 className="text-2xl font-bold text-white tracking-tight">{formData.name}</h2>
                <p className="text-blue-100 text-sm font-medium mt-1 opacity-90">{formData.designation} • {formData.department}</p>
            </div>
        </div>
    );
}

function TabButton({ activeTab, setActiveTab, id, label }) {
    return <button type="button" onClick={() => setActiveTab(id)} className={`whitespace-nowrap py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === id ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>;
}

function RecognitionTab({ formData, trophies }) {
    const badges = formData.badges || {};
    return (
        <div className="space-y-8">
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex items-center">
                    <div className="p-3 bg-white rounded-full text-blue-500 shadow-sm mr-4"><TrendingUpIcon /></div>
                    <div><p className="text-xs font-bold text-gray-500 uppercase">Total Points</p><p className="text-2xl font-extrabold text-blue-600">{Number(formData.points) || 0}</p></div>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 flex items-center">
                    <div className="p-3 bg-white rounded-full text-purple-500 shadow-sm mr-4"><StarIcon /></div>
                    <div>
                        <p className="text-xs font-bold text-gray-500 uppercase">Status</p>
                        <p className="text-xl font-extrabold text-purple-600 truncate" title={getStatusLabel(formData.points)}>
                            {getStatusLabel(formData.points)}
                        </p>
                    </div>
                </div>
            </div>

            <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Badges</h4>
                <div className="flex space-x-6">
                    <BadgeDisplay count={Number(badges.commitment || badges.Commitment)} color={BRAND_COLORS.orange} label="Commitment" />
                    <BadgeDisplay count={Number(badges.collaboration || badges.Collaboration)} color={BRAND_COLORS.teal} label="Collaboration" />
                    <BadgeDisplay count={Number(badges.customer || badges.Customer)} color={BRAND_COLORS.red} label="Customer" />
                </div>
            </div>

            <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Trophies Won</h4>
                {trophies && trophies.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {trophies.map(trophy => (
                            <div key={trophy.id} className="flex items-center p-3 border border-yellow-200 bg-yellow-50 rounded-lg">
                                <div className="p-2 bg-white rounded-full text-yellow-500 shadow-sm mr-3">
                                    <TrophyIcon className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-gray-800 leading-tight">{trophy.title}</p>
                                    <p className="text-xs text-gray-500 mt-0.5 flex items-center">
                                        Won {displayDate(trophy.endDate)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-400 italic">No trophies yet.</p>
                )}
            </div>
        </div>
    );
}

function DocumentsManagerTab({ documents, isLoading, docUpload, setDocUpload, onUpload, onDelete, readOnly, isSaving }) {
    return (
        <div className="space-y-4">
            {!readOnly && (
                <div className="bg-gray-50 p-4 rounded-xl border border-dashed border-gray-300">
                    <div className="flex items-center space-x-3">
                        <input type="text" placeholder="Document Title (e.g. Offer Letter)" value={docUpload.title} onChange={e => setDocUpload(prev => ({ ...prev, title: e.target.value }))} className="flex-1 p-2 border border-gray-300 rounded-lg text-sm" />
                        <input type="file" onChange={e => setDocUpload(prev => ({ ...prev, file: e.target.files[0] }))} className="text-sm text-gray-500" />
                        <button type="button" onClick={onUpload} disabled={isSaving || !docUpload.file || !docUpload.title} className="px-4 py-2 bg-green-600 text-white text-sm font-bold rounded-lg disabled:bg-gray-400">Upload</button>
                    </div>
                </div>
            )}
            <div className="space-y-2">
                {isLoading ? <p className="text-center text-sm text-gray-500">Loading...</p> : (
                    documents.length > 0 ? documents.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition">
                            <a href={doc.url} target="_blank" rel="noopener noreferrer" className="flex items-center flex-1 text-blue-600 hover:underline">
                                <PaperclipIcon className="w-5 h-5 text-gray-400 mr-3" />
                                <div><p className="font-medium text-sm text-gray-800">{doc.title}</p><p className="text-xs text-gray-400">{doc.fileName}</p></div>
                            </a>
                            <span className="text-xs text-gray-400 mr-4">{displayDate(doc.uploadedAt)}</span>
                            {!readOnly && <button type="button" onClick={() => onDelete(doc)} className="text-red-400 hover:text-red-600 p-2"><TrashIcon className="w-4 h-4" /></button>}
                        </div>
                    )) : <p className="text-sm text-gray-400 text-center py-4">No documents found.</p>
                )}
            </div>
        </div>
    );
}

function OfficialInfoTab({ formData, handleChange, readOnly, allUsers, existingUser }) {
    const [managerSearch, setManagerSearch] = useState('');
    const [showManagerSuggestions, setShowManagerSuggestions] = useState(false);
    const searchRef = useRef(null);

    useEffect(() => {
        if (formData.managerId && allUsers) {
            const manager = allUsers.find(u => u.id === formData.managerId);
            if (manager) setManagerSearch(manager.name);
        }
    }, [formData.managerId, allUsers]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowManagerSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredManagers = allUsers?.filter(u => 
        u.id !== existingUser.id && 
        u.name.toLowerCase().includes(managerSearch.toLowerCase())
    ).slice(0, 5) || [];

    const handleManagerSelect = (manager) => {
        handleChange({ target: { name: 'managerId', value: manager.id } });
	handleChange({ target: { name: 'managerName', value: manager.name } });
        setManagerSearch(manager.name);
        setShowManagerSuggestions(false);
    };

    const handleClearManager = () => {
        handleChange({ target: { name: 'managerId', value: '' } });
        handleChange({ target: { name: 'managerName', value: '' } });
        setManagerSearch('');
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <InputField label="Full Name" value={formData.name} disabled />
            <InputField label="Work Email" value={formData.email} disabled />
            <InputField label="Department" value={formData.department} disabled />
            <InputField label="Employee ID" name="employeeId" value={formData.employeeId} onChange={handleChange} disabled={readOnly} />
            <InputField label="Designation" name="designation" value={formData.designation} onChange={handleChange} disabled={readOnly} />
            
            <div className="relative" ref={searchRef}>
                <label className="block text-xs font-medium text-gray-500 mb-1">Manager</label>
                <div className={`relative flex items-center border rounded-lg bg-white ${readOnly ? 'bg-gray-100 border-gray-200' : 'border-gray-300 focus-within:ring-2 focus-within:ring-blue-100'}`}>
                    <SearchIcon className="h-4 w-4 text-gray-400 ml-2" />
                    <input 
                        type="text"
                        className="w-full p-2 text-sm outline-none bg-transparent"
                        placeholder="Search manager..."
                        value={managerSearch}
                        onChange={(e) => { setManagerSearch(e.target.value); setShowManagerSuggestions(true); }}
                        onFocus={() => !readOnly && setShowManagerSuggestions(true)}
                        disabled={readOnly}
                    />
                    {!readOnly && formData.managerId && (
                        <button type="button" onClick={handleClearManager} className="p-1 hover:text-red-500 mr-1">
                            <XIcon className="h-4 w-4 text-gray-400" />
                        </button>
                    )}
                </div>

                {showManagerSuggestions && !readOnly && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredManagers.length > 0 ? (
                            filteredManagers.map(manager => (
                                <div 
                                    key={manager.id} 
                                    onClick={() => handleManagerSelect(manager)}
                                    className="p-2 hover:bg-blue-50 cursor-pointer flex items-center border-b border-gray-50 last:border-0"
                                >
                                    <UserAvatar url={manager.photoURL} name={manager.name} className="h-6 w-6 mr-2" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">{manager.name}</p>
                                        <p className="text-xs text-gray-400">{manager.department}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-3 text-xs text-gray-400 text-center">No matches found</div>
                        )}
                    </div>
                )}
            </div>

            <InputField label="Date of Joining" type="date" name="dateOfJoining" value={formData.dateOfJoining} onChange={handleChange} disabled={readOnly} />
            <InputField label="Confirmation Date" type="date" name="confirmationDate" value={formData.confirmationDate} onChange={handleChange} disabled={readOnly} />
            <InputField label="Location" name="location" value={formData.location} onChange={handleChange} disabled={readOnly} />
            <InputField label="Level" name="employmentLevel" value={formData.employmentLevel} onChange={handleChange} disabled={readOnly} />
            <InputField label="Grade" name="employmentGrade" value={formData.employmentGrade} onChange={handleChange} disabled={readOnly} />
            <SelectField label="Status" name="employeeStatus" value={formData.employeeStatus} onChange={handleChange} disabled={readOnly} options={['Active', 'Inactive', 'On-Notice']} />
        </div>
    );
}

function PersonalInfoTab({ formData, handleChange, readOnly }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <InputField label="Middle Name" name="middleName" value={formData.middleName} onChange={handleChange} disabled={readOnly} />
            <InputField label="Parent Name" name="parentName" value={formData.parentName} onChange={handleChange} disabled={readOnly} />
            <SelectField label="Gender" name="gender" value={formData.gender} onChange={handleChange} disabled={readOnly} options={['Male', 'Female', 'Other']} />
            <InputField label="Date of Birth" type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} disabled={readOnly} />
            <SelectField label="Marital Status" name="maritalStatus" value={formData.maritalStatus} onChange={handleChange} disabled={readOnly} options={['Single', 'Married', 'Divorced', 'Widowed']} />
            <InputField label="Blood Group" name="bloodGroup" value={formData.bloodGroup} onChange={handleChange} disabled={readOnly} />
            <SelectField label="Differently Abled" name="isDifferentlyAbled" value={formData.isDifferentlyAbled} onChange={handleChange} disabled={readOnly} options={['No', 'Yes']} />
            <div className="lg:col-span-2"><InputField label="Photo URL" name="photoURL" value={formData.photoURL} onChange={handleChange} disabled={readOnly} placeholder="https://..." /></div>
        </div>
    );
}

function ContactInfoTab({ formData, handleChange, readOnly }) {
    return (
        <div className="space-y-6">
            <Section title="Contact Details">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex gap-2">
                        <div className="w-1/4"><InputField label="Code" name="phoneCountryCode" value={formData.phoneCountryCode} onChange={handleChange} disabled={readOnly} /></div>
                        <div className="w-3/4"><InputField label="Mobile Number" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} disabled={readOnly} /></div>
                    </div>
                    <InputField label="Personal Email" name="personalEmail" value={formData.personalEmail} onChange={handleChange} disabled={readOnly} />
                    <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Residential Address</label>
                        <textarea name="residentialAddress" value={formData.residentialAddress} onChange={handleChange} disabled={readOnly} rows="2" className="w-full p-2 border rounded-lg text-sm" />
                    </div>
                </div>
            </Section>
            <Section title="Identity">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField label="Nationality" name="nationality" value={formData.nationality} onChange={handleChange} disabled={readOnly} />
                    <InputField label="Aadhar" name="aadharNumber" value={formData.aadharNumber} onChange={handleChange} disabled={readOnly} />
                    <InputField label="PAN Card" name="panCardNumber" value={formData.panCardNumber} onChange={handleChange} disabled={readOnly} />
                    <InputField label="Passport" name="passportNumber" value={formData.passportNumber} onChange={handleChange} disabled={readOnly} />
                </div>
            </Section>
            <Section title="Emergency Contact">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <InputField label="Name" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} disabled={readOnly} />
                    <InputField label="Relation" name="emergencyContactRelation" value={formData.emergencyContactRelation} onChange={handleChange} disabled={readOnly} />
                    <InputField label="Phone" name="emergencyContactNumber" value={formData.emergencyContactNumber} onChange={handleChange} disabled={readOnly} />
                </div>
            </Section>
        </div>
    );
}

function PerformanceTab({ reviews }) {
    if (reviews.length === 0) return <div className="text-center py-10 text-gray-400">No completed reviews found.</div>;
    return (
        <div className="space-y-3">
            {reviews.map(review => (
                <div key={review.id} className="bg-white p-4 rounded-lg border border-gray-200 flex justify-between items-center shadow-sm">
                    <div>
                        <p className="font-bold text-[#213368]">{review.cycle}</p>
                        <p className="text-xs text-gray-500">Completed: {displayDate(review.updatedAt)}</p>
                    </div>
                    <div className="text-right">
                        <span className="block text-xl font-bold text-green-600">{review.finalRating || 'N/A'}</span>
                        <span className="text-xs text-gray-400 uppercase">Rating</span>
                    </div>
                </div>
            ))}
        </div>
    );
}

function ExitTab({ formData, handleChange, readOnly }) {
    
    const handleCancelExit = () => {
        if (window.confirm("Are you sure you want to cancel this employee's exit? This will clear the last working date and reset the offboarding process.")) {
            handleChange({ target: { name: 'lastWorkingDate', value: '' } });
        }
    };

    // Calculate current status for the UI (with a fallback for legacy data)
    const c = formData.exitClearance || {
        manager: { status: 'Pending' },
        it: { status: 'Pending' },
        hr: { status: 'Pending' },
        finance: { status: 'Pending' },
        overallStatus: 'Pending'
    };

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center mb-4 border-b pb-2">
                    <h3 className="text-lg font-bold text-[#213368]">Offboarding Details</h3>
                    
                    {formData.lastWorkingDate && (
                        <div className="flex items-center space-x-4">
                            <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-bold rounded-full">
                                Exit Initiated
                            </span>
                            {!readOnly && (
                                <button 
                                    type="button" 
                                    onClick={handleCancelExit}
                                    className="text-xs font-bold text-red-500 hover:text-red-700 hover:underline transition"
                                >
                                    Cancel Exit
                                </button>
                            )}
                        </div>
                    )}
                </div>
                
                <p className="text-sm text-gray-500 mb-6">
                    Setting a Last Working Date is the first step in the offboarding process.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField 
                        label="Last Working Date" 
                        type="date" 
                        name="lastWorkingDate" 
                        value={formData.lastWorkingDate || ''} 
                        onChange={handleChange} 
                        disabled={readOnly} 
                    />
                </div>
            </div>

            {/* LIVE CLEARANCE STATUS DASHBOARD */}
            {formData.lastWorkingDate && (
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mt-6">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Clearance Status</h4>
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${c.overallStatus === 'Cleared' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            Overall: {c.overallStatus}
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <StatusRow label="Reporting Manager" status={c.manager?.status || 'Pending'} />
                        <StatusRow label="IT" status={c.it?.status || 'Pending'} />
                        <StatusRow label="Accounts & Finance" status={c.finance?.status || 'Pending'} />
                        <StatusRow label="HR" status={c.hr?.status || 'Pending'} />
                    </div>
                    <p className="text-xs text-gray-500 mt-4 italic">
                        * Clearance sign-offs are managed by respective departments in the HR Center's Exit Management tab.
                    </p>
                </div>
            )}
        </div>
    );
}

// Small helper component for the status rows
function StatusRow({ label, status }) {
    return (
        <div className="flex justify-between items-center bg-white p-3 rounded border border-gray-100 shadow-sm">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            <span className={`text-xs font-bold ${status === 'Cleared' ? 'text-green-600' : 'text-yellow-600'}`}>{status}</span>
        </div>
    );
}

// ==========================================
// EMPLOYEE JOURNEY TAB
// ==========================================
function JourneyTab({ formData, handleChange, readOnly }) {
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [newMilestone, setNewMilestone] = useState({
        type: 'Promotion',
        date: safeDate(new Date()),
        title: '',
        description: '',
        newDesignation: '' 
    });

    // --- AUTO-INJECT JOINING & EXIT DATES ---
    const journey = formData.journey || [];
    let combinedJourney = [...journey];
    
    if (formData.dateOfJoining) {
        combinedJourney.push({
            id: 'auto-joining',
            type: 'Joining',
            date: formData.dateOfJoining,
            title: 'Joined the Company',
            description: 'Initial onboarding and start of the employee journey.',
            isAuto: true
        });
    }

    if (formData.lastWorkingDate) {
        const isFuture = new Date(formData.lastWorkingDate) > new Date();
        combinedJourney.push({
            id: 'auto-exit',
            type: 'Exit',
            date: formData.lastWorkingDate,
            title: isFuture ? 'Scheduled Exit' : 'Left the Company',
            description: 'End of the employee journey and transition to alumni.',
            isAuto: true
        });
    }
    
    // Sort descending by date
    const sortedJourney = combinedJourney.sort((a, b) => new Date(b.date) - new Date(a.date));

    // --- HANDLE SAVE & AUTO-UPDATE DESIGNATION ---
    const handleSave = () => {
        // 1. CONDITIONAL VALIDATION
        if (!newMilestone.date) return alert("Date is required.");
        
        if (newMilestone.type === 'Promotion' && !newMilestone.newDesignation.trim()) {
            return alert("New Designation is required for a promotion.");
        }
        if (newMilestone.type !== 'Promotion' && !newMilestone.title.trim()) {
            return alert("Headline / Title is required.");
        }
        
        // 2. DATE BOUNDARY VALIDATION
        const mDate = new Date(newMilestone.date);
        mDate.setHours(0,0,0,0);

        if (formData.dateOfJoining) {
            const doj = new Date(formData.dateOfJoining);
            doj.setHours(0,0,0,0);
            if (mDate < doj) return alert("A milestone cannot occur before the employee's Joining Date.");
        }

        if (formData.lastWorkingDate) {
            const lwd = new Date(formData.lastWorkingDate);
            lwd.setHours(0,0,0,0);
            if (mDate > lwd) return alert("A milestone cannot occur after the employee's Exit Date.");
        }

        // 3. AUTO-GENERATE TITLE FOR PROMOTIONS
        let finalTitle = newMilestone.title;
        if (newMilestone.type === 'Promotion') {
            finalTitle = `Promoted to ${newMilestone.newDesignation}`;
        }

        // 4. AUTO-UPDATE CURRENT DESIGNATION
        if (newMilestone.type === 'Promotion' && newMilestone.newDesignation) {
            const isLatestPromotion = journey
                .filter(m => m.type === 'Promotion' && m.id !== editingId)
                .every(m => new Date(newMilestone.date) >= new Date(m.date));
                
            if (isLatestPromotion) {
                // Instantly syncs with the Official tab
                handleChange({ target: { name: 'designation', value: newMilestone.newDesignation } });
            }
        }

        // 5. SAVE LOGIC
        const currentUser = auth.currentUser?.displayName || 'HR Admin';
        const auditData = {
            updatedBy: currentUser,
            updatedAt: new Date().toISOString()
        };

        const milestoneToSave = { 
            ...newMilestone, 
            title: finalTitle, 
            ...auditData 
        };

        if (editingId) {
            const updatedJourney = journey.map(m => 
                m.id === editingId ? { ...milestoneToSave, id: editingId } : m
            );
            handleChange({ target: { name: 'journey', value: updatedJourney } });
        } else {
            const milestone = { ...milestoneToSave, id: Date.now().toString() + Math.random().toString(36).substr(2, 5) };
            handleChange({ target: { name: 'journey', value: [...journey, milestone] } });
        }
        
        handleCancel();
    };

    const handleEdit = (item) => {
        setNewMilestone({
            type: item.type,
            date: item.date,
            title: item.title,
            description: item.description || '',
            newDesignation: item.newDesignation || ''
        });
        setEditingId(item.id);
        setShowForm(true);
    };

    const handleDelete = (id) => {
        if (!window.confirm("Delete this milestone?")) return;
        handleChange({ target: { name: 'journey', value: journey.filter(m => m.id !== id) } });
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setNewMilestone({ type: 'Promotion', date: safeDate(new Date()), title: '', description: '', newDesignation: '' });
    };

    // --- LINC THEME COLORS ---
    const getMilestoneStyling = (type) => {
        switch (type) {
            case 'Joining': return { dot: 'border-[#20BEC6]', bg: 'bg-[#20BEC6]/10', text: 'text-[#20BEC6]' }; 
            case 'Promotion': return { dot: 'border-[#213368]', bg: 'bg-[#213368]/10', text: 'text-[#213368]' }; 
            case 'Compensation Change': return { dot: 'border-[#F4A866]', bg: 'bg-[#F4A866]/10', text: 'text-[#F4A866]' }; 
            case 'Long Leave': return { dot: 'border-[#D95448]', bg: 'bg-[#D95448]/10', text: 'text-[#D95448]' }; 
            case 'Relocation': return { dot: 'border-blue-500', bg: 'bg-blue-50', text: 'text-blue-700' };
            case 'Exit': return { dot: 'border-gray-600', bg: 'bg-gray-100', text: 'text-gray-800' }; 
            default: return { dot: 'border-gray-400', bg: 'bg-gray-50', text: 'text-gray-700' };
        }
    };

    return (
        <div className="space-y-6">
            
            {/* Header Actions */}
            {!readOnly && (
                <div className="flex justify-end">
                    <button 
                        type="button" 
                        onClick={() => showForm ? handleCancel() : setShowForm(true)} 
                        className="px-4 py-2 bg-[#213368] text-white text-sm font-bold rounded-lg hover:bg-blue-900 transition shadow-sm"
                    >
                        {showForm ? 'Cancel' : '+ Add Milestone'}
                    </button>
                </div>
            )}

            {/* Add / Edit Milestone Form */}
            {showForm && !readOnly && (
                <div className="bg-white p-5 rounded-xl border border-[#20BEC6] shadow-sm space-y-4 mb-6 relative">
                    <h3 className="text-sm font-bold text-[#213368] border-b pb-2">
                        {editingId ? 'Edit Milestone' : 'Log New Milestone'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <SelectField 
                            label="Type" 
                            value={newMilestone.type} 
                            onChange={e => setNewMilestone({...newMilestone, type: e.target.value})}
                            options={['Promotion', 'Compensation Change', 'Relocation', 'Long Leave', 'Other']}
                        />
                        <InputField 
                            label="Date" 
                            type="date" 
                            value={newMilestone.date} 
                            onChange={e => setNewMilestone({...newMilestone, date: e.target.value})} 
                        />
                        
                        {/* CONDITIONAL INPUT: Promotion vs Everything Else */}
                        {newMilestone.type === 'Promotion' ? (
                            <div className="md:col-span-2 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                <InputField 
                                    label="New Designation (This automatically updates their Official Profile)" 
                                    value={newMilestone.newDesignation} 
                                    onChange={e => setNewMilestone({...newMilestone, newDesignation: e.target.value})} 
                                    placeholder="e.g. Lead Engineer"
                                />
                            </div>
                        ) : (
                            <div className="md:col-span-2">
                                <InputField 
                                    label="Headline / Title (e.g. 10% Increment, Moved to London Office)" 
                                    value={newMilestone.title} 
                                    onChange={e => setNewMilestone({...newMilestone, title: e.target.value})} 
                                    placeholder="Enter a brief title..."
                                />
                            </div>
                        )}

                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-500 mb-1">Additional Details (Optional)</label>
                            <textarea 
                                rows="2" 
                                value={newMilestone.description}
                                onChange={e => setNewMilestone({...newMilestone, description: e.target.value})}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#20BEC6]"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end pt-2">
                        <button type="button" onClick={handleSave} className="px-4 py-2 bg-green-600 text-white text-sm font-bold rounded-lg hover:bg-green-700 transition shadow-sm">
                            {editingId ? 'Update Milestone' : 'Save to Journey'}
                        </button>
                    </div>
                </div>
            )}

            {/* The Timeline UI */}
            <div className="relative">
                <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-gray-200"></div>
                
                {sortedJourney.length === 0 ? (
                    <p className="pl-10 text-sm text-gray-400 italic py-4">No milestones added yet. Ensure the employee has a Joining Date set!</p>
                ) : (
                    <div className="space-y-6">
                        {sortedJourney.map((item) => {
                            const styles = getMilestoneStyling(item.type);
                            return (
                                <div key={item.id} className="relative pl-10">
                                    <div className={`absolute left-0 top-1.5 h-6 w-6 rounded-full border-4 bg-white ${styles.dot} shadow-sm z-10`}></div>
                                    
                                    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition group flex flex-col md:flex-row md:items-stretch gap-4">
                                        
                                        {/* Left Side: Core Info */}
                                        <div className="md:w-1/3 flex flex-col justify-start border-b md:border-b-0 md:border-r border-gray-100 pb-3 md:pb-0 md:pr-4">
                                            <div className="flex items-center space-x-2 mb-2">
                                                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${styles.bg} ${styles.text}`}>
                                                    {item.type}
                                                </span>
                                                <span className="text-xs font-medium text-gray-400">{displayDate(item.date)}</span>
                                            </div>
                                            <h4 className="text-base font-bold text-[#213368] leading-snug">{item.title}</h4>
                                        </div>

                                        {/* Right Side: Description & Audit */}
                                        <div className="md:w-2/3 flex flex-col justify-between">
                                            {item.description ? (
                                                <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.description}</p>
                                            ) : (
                                                <p className="text-sm text-gray-400 italic">No additional details provided.</p>
                                            )}
                                            
                                            <div className="flex justify-between items-end mt-4 pt-3 border-t border-gray-50">
                                                {item.updatedBy ? (
                                                    <p className="text-[10px] text-gray-400 font-medium">
                                                        Updated by {item.updatedBy} on {new Date(item.updatedAt).toLocaleDateString()}
                                                    </p>
                                                ) : (
                                                    <div></div>
                                                )}
                                                
                                                {/* Action Buttons */}
                                                {!readOnly && !item.isAuto && (
                                                    <div className="flex space-x-4 opacity-0 group-hover:opacity-100 transition">
                                                        <button 
                                                            type="button" 
                                                            onClick={() => handleEdit(item)} 
                                                            className="text-gray-400 hover:text-[#20BEC6] transition text-xs font-bold"
                                                        >
                                                            EDIT
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            onClick={() => handleDelete(item.id)} 
                                                            className="text-gray-400 hover:text-red-500 transition"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

const Section = ({ title, children }) => <fieldset className="border border-gray-200 p-4 rounded-xl mb-4 bg-white"><legend className="text-xs font-bold text-gray-500 px-2 uppercase">{title}</legend>{children}</fieldset>;
const InputField = ({ label, type = "text", ...props }) => <div><label className="block text-xs font-medium text-gray-500 mb-1">{label}</label><input type={type} className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-100 outline-none transition" {...props} /></div>;
const SelectField = ({ label, options, ...props }) => <div><label className="block text-xs font-medium text-gray-500 mb-1">{label}</label><select className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white" {...props}><option value="">Select...</option>{options.map(o => <option key={o}>{o}</option>)}</select></div>;
const BadgeDisplay = ({ count, color, label }) => <div className={`flex flex-col items-center ${!count ? 'opacity-40 grayscale' : ''}`}><div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm" style={{ backgroundColor: count ? color : '#ccc' }}>{count || 0}</div><span className="text-[10px] font-bold text-gray-500 mt-1">{label}</span></div>;
const ReadOnlyField = ({ label, value }) => <div><label className="block text-xs font-medium text-gray-500 mb-1">{label}</label><div className="p-2 bg-gray-100 rounded-lg text-sm text-gray-700 border border-gray-200">{value || '-'}</div></div>;